/**
 * @spec docs/superpowers/specs/2026-10-13-wallet-network-awareness-design.md §4.3
 * Chain data with no React or app dependencies.
 *
 * Split out of chains.ts so network-gating code can read real chain data even in the many tests
 * that replace '@/lib/chains' with a partial vi.mock factory (reading an export missing from such a
 * factory throws). chains.ts re-exports everything here, so existing importers see the same objects.
 */
import { env } from './env';
import { PUBLIC_RPC_URLS } from './public-rpc';

/**
 * Chain configuration
 */
export interface ChainConfig {
  id: number;
  name: string;
  rpcUrl: string;
  explorerUrl: string;
  explorerName: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

/**
 * Supported chains.
 *
 * Polygon was removed from the supported set as part of the CHIP migration.
 * `getChainById` and `isSupportedChain` now only see Base. Privy's chain list
 * (lib/privy.ts) keeps Polygon so legacy USDT can still be sent. The legacy Polygon explorer URL (still needed by the bridge / swap /
 * USDT-redemption views until they are torn down by the cross-chain decom
 * workstream) is exposed separately as `LEGACY_POLYGON_CONFIG` so it
 * intentionally does NOT appear in supported-chain checks.
 */
export const CHAIN_CONFIG = {
  BASE: {
    id: 8453,
    name: 'Base',
    rpcUrl: PUBLIC_RPC_URLS[8453],
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
  BASE_SEPOLIA: {
    id: 84532,
    name: 'Base Sepolia',
    rpcUrl: PUBLIC_RPC_URLS[84532],
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
} as const satisfies Record<string, ChainConfig>;

/**
 * @deprecated Legacy Polygon mainnet metadata for the bridge / swap / USDT
 * redemption views. Slated for deletion by the cross-chain decom workstream.
 * Intentionally not part of `CHAIN_CONFIG` so `isSupportedChain` correctly
 * returns false for chainId 137.
 */
export const LEGACY_POLYGON_CONFIG: ChainConfig = {
  id: 137,
  name: 'Polygon',
  rpcUrl: PUBLIC_RPC_URLS[137],
  explorerUrl: env.polygonExplorerUrl,
  explorerName: 'Polygonscan',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
};

/**
 * Get the chain ID for staking operations (Base mainnet or testnet)
 */
export function getStakingChainId(): number {
  return env.baseChainId === CHAIN_CONFIG.BASE_SEPOLIA.id
    ? CHAIN_CONFIG.BASE_SEPOLIA.id
    : CHAIN_CONFIG.BASE.id;
}

/**
 * Chains the dapp may ask a wallet to switch to. Deliberately wider than isSupportedChain:
 * the legacy USDT claim signs a Polygon domain, so its form must be able to request Polygon,
 * while Polygon must stay out of every "supported chain" check.
 */
const SWITCHABLE_CHAINS: readonly ChainConfig[] = [
  CHAIN_CONFIG.BASE,
  CHAIN_CONFIG.BASE_SEPOLIA,
  LEGACY_POLYGON_CONFIG,
];

export function getSwitchableChain(chainId: number): ChainConfig | undefined {
  return SWITCHABLE_CHAINS.find((chain) => chain.id === chainId);
}

/** Used when the env explorer URL is missing or not https (L-005). */
const FALLBACK_EXPLORERS: ReadonlyArray<readonly [number, string]> = [
  [CHAIN_CONFIG.BASE.id, 'https://basescan.org'],
  [CHAIN_CONFIG.BASE_SEPOLIA.id, 'https://sepolia.basescan.org'],
  [LEGACY_POLYGON_CONFIG.id, 'https://polygonscan.com'],
];

/** The block explorer of any chain the dapp touches (Polygon included), https only; null if none. */
function explorerBase(chainId: number): string | null {
  const configured = getSwitchableChain(chainId)?.explorerUrl;
  const base = configured?.startsWith('https://')
    ? configured
    : FALLBACK_EXPLORERS.find(([id]) => id === chainId)?.[1];
  return base ? base.replace(/\/$/, '') : null;
}

export function explorerTxUrl(chainId: number, hash: string): string | null {
  const base = explorerBase(chainId);
  return base ? `${base}/tx/${hash}` : null;
}

export function explorerAddressUrl(chainId: number, address: string): string | null {
  const base = explorerBase(chainId);
  return base ? `${base}/address/${address}` : null;
}

/** Human name for a chain id; unknown chains read "Chain {id}". */
export function getChainDisplayName(chainId: number): string {
  return getSwitchableChain(chainId)?.name ?? `Chain ${chainId}`;
}

/** The chain a token's network runs on: Base follows the build (Base or Base Sepolia). */
export function getNetworkChainId(network: 'base' | 'polygon'): number {
  return network === 'base' ? getStakingChainId() : LEGACY_POLYGON_CONFIG.id;
}
